/**
 * QRScanDialog — modale plein écran pour scanner un QR code
 * d'étiquette de validation RCP. Décode en temps réel via jsQR,
 * puis renvoie le numéro RCP extrait au parent.
 *
 * Usage :
 *   <QRScanDialog
 *     open={open}
 *     onClose={() => setOpen(false)}
 *     onDetected={(rcpNumber) => setSearchQuery(rcpNumber)}
 *   />
 *
 * Comportement :
 * - getUserMedia rear camera (`facingMode: environment`)
 * - Boucle de scan via requestAnimationFrame + jsQR
 * - Si QR détecté → extrait le pattern RCP-MMYYYY-XXXX-XX de l'URL ou du texte
 * - Vibration courte + son scan beep + auto-close
 */
import React, { useEffect, useRef, useState } from 'react';
import jsQR from 'jsqr';
import { X, Loader2, ScanLine } from 'lucide-react';
import { Button } from '../../ui/button';
import { toast } from 'sonner';

const RCP_REGEX = /RCP-\d{6}-\d{4}-[A-Z]{2}/i;

const QRScanDialog = ({ open, onClose, onDetected }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const detectedRef = useRef(false);
  const [stream, setStream] = useState(null);
  const [cameraError, setCameraError] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    detectedRef.current = false;

    (async () => {
      try {
        const ms = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        });
        if (!active) {
          ms.getTracks().forEach((t) => t.stop());
          return;
        }
        setStream(ms);
        if (videoRef.current) {
          videoRef.current.srcObject = ms;
          await videoRef.current.play().catch(() => {});
        }
        scanLoop();
      } catch (e) {
        setCameraError(e?.message || 'Caméra inaccessible');
      }
    })();

    return () => {
      active = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (stream) stream.getTracks().forEach((t) => t.stop());
      setStream(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const scanLoop = () => {
    if (detectedRef.current) return;
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c || v.readyState < 2) {
      rafRef.current = requestAnimationFrame(scanLoop);
      return;
    }
    const w = v.videoWidth;
    const h = v.videoHeight;
    if (!w || !h) {
      rafRef.current = requestAnimationFrame(scanLoop);
      return;
    }
    c.width = w;
    c.height = h;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(v, 0, 0, w, h);
    try {
      const img = ctx.getImageData(0, 0, w, h);
      const code = jsQR(img.data, w, h, { inversionAttempts: 'dontInvert' });
      if (code && code.data) {
        const data = code.data;
        const m = data.match(RCP_REGEX);
        if (m) {
          detectedRef.current = true;
          // Feedback : vibration + son court
          try { if (navigator.vibrate) navigator.vibrate(80); } catch {/* ignore */}
          onDetected(m[0].toUpperCase());
          onClose();
          return;
        }
        // QR détecté mais pas un RCP de la pharmacie
        toast.warning('QR détecté mais pas un numéro RCP valide');
      }
    } catch {/* image data error — continue */}
    rafRef.current = requestAnimationFrame(scanLoop);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black/95 flex flex-col" data-testid="qr-scan-dialog">
      {/* Header */}
      <div className="flex items-center justify-between p-4 text-white">
        <div className="flex items-center gap-2">
          <ScanLine className="w-5 h-5" />
          <span className="font-semibold">Scanner un QR de réception</span>
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition"
          data-testid="qr-scan-close"
          aria-label="Fermer"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Camera viewport */}
      <div className="relative flex-1 flex items-center justify-center overflow-hidden">
        {cameraError ? (
          <div className="text-center text-white p-6 max-w-sm">
            <p className="text-lg font-semibold mb-2">Caméra inaccessible</p>
            <p className="text-sm text-white/70">{cameraError}</p>
            <p className="text-xs text-white/50 mt-3">
              Autorisez l'accès caméra dans les réglages du navigateur.
            </p>
          </div>
        ) : !stream ? (
          <div className="text-white flex flex-col items-center gap-2">
            <Loader2 className="w-8 h-8 animate-spin" />
            <span className="text-sm">Activation caméra…</span>
          </div>
        ) : (
          <>
            <video
              ref={videoRef}
              playsInline
              muted
              className="w-full h-full object-cover"
              data-testid="qr-scan-video"
            />
            {/* Cadre de visée */}
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <div className="w-64 h-64 sm:w-72 sm:h-72 border-4 border-[#0F766E] rounded-2xl shadow-[0_0_0_9999px_rgba(0,0,0,0.4)] relative">
                <div className="absolute -top-1 -left-1 w-8 h-8 border-t-4 border-l-4 border-white rounded-tl-2xl" />
                <div className="absolute -top-1 -right-1 w-8 h-8 border-t-4 border-r-4 border-white rounded-tr-2xl" />
                <div className="absolute -bottom-1 -left-1 w-8 h-8 border-b-4 border-l-4 border-white rounded-bl-2xl" />
                <div className="absolute -bottom-1 -right-1 w-8 h-8 border-b-4 border-r-4 border-white rounded-br-2xl" />
              </div>
            </div>
          </>
        )}
        <canvas ref={canvasRef} className="hidden" />
      </div>

      {/* Footer hint */}
      <div className="p-4 text-center text-white/80 text-sm">
        <p>Centrez le QR de l'étiquette dans le cadre.</p>
        <p className="text-xs text-white/50 mt-1">La détection est automatique.</p>
        <Button
          onClick={onClose}
          variant="ghost"
          className="mt-3 text-white hover:bg-white/10"
          data-testid="qr-scan-cancel-btn"
        >
          Annuler
        </Button>
      </div>
    </div>
  );
};

export default QRScanDialog;
